import { PRE_ORDER_TABLE } from "./resources/orders/pre-order/db"
import { PRE_ORDER_FLOW_TABLE } from "./resources/orders/pre-order-flow/db"
import { CHANGE_QUEUE_TABLE } from "./change-queue"
import type { HttpClientAdapter, StorageAdapter } from "./types"

/**
 * Shared hydration path for the pre-order ON-DEMAND fetchers (STK-1323 window,
 * STK-1972 by-id). Both fetch `{ preOrders, flows }` from a sync-shape endpoint
 * and upsert them into Dexie below the kept floor; everything after the HTTP
 * call — the change-queue shield, the writes, the `_ephemeral` stamping — is
 * identical and lives here.
 *
 * The one divergence is `atomic`, and it is deliberate, NOT a default to
 * flip lightly:
 *
 *  - By-id (`atomic: true`) — ONE order + its flows. Wrapping that tiny batch
 *    in a transaction makes the shield check-and-write race-free and prevents
 *    a half-written order (header without lines), at negligible lock cost.
 *    Two invariants depend on it:
 *      1. Partial writes: sequential writes could commit the parent PO but
 *         fail on the flows — the focus route's reactive existence query would
 *         resolve `present` and render an order missing its lines — or land
 *         the rows but drop the `_ephemeral` markers, stranding the data
 *         outside the TTL/LRU bookkeeping that evicts it.
 *      2. Shield TOCTOU: reading `getActiveEntityIds` OUTSIDE the transaction
 *         leaves a window where an edit queued after the snapshot but before
 *         the writes is not shielded, and the server copy clobbers an unsynced
 *         local change. With `_changeQueue` in the transaction scope, Dexie
 *         serializes overlapping `rw` transactions: a concurrent enqueue
 *         either commits before this read (shielded) or after this commit
 *         (applies on top of the fresh server copy).
 *
 *  - Window (`atomic: false`) — potentially HUNDREDS of orders per fetch. A
 *    single `rw` transaction over `preOrders` + `preOrderFlows` + `_ephemeral`
 *    + `_changeQueue` would hold those tables for the whole bulk write, and
 *    every user edit (change-queue enqueue) or SSE sync write would queue
 *    behind it. The window path therefore keeps its original shipped
 *    behavior: shield snapshot, then plain sequential bulk writes.
 */

const EPHEMERAL_TABLE = "_ephemeral"

export interface OnDemandDeps {
  storage: StorageAdapter
  httpClient: HttpClientAdapter
  /** Ids with an in-flight local change — never overwritten. */
  getActiveEntityIds: (resource: string) => Promise<Set<string>>
  /** Network reachability (injectable for tests). */
  isOnline: () => boolean
}

export interface PreOrderHydrationRows {
  preOrders: Array<{ id: string }>
  flows: Array<{ id: string }>
}

export interface PreOrderHydrationOutcome {
  /** Pre-orders actually written (shielded rows excluded). */
  pos: Array<{ id: string }>
  /** Flows actually written (shielded rows excluded). */
  flows: Array<{ id: string }>
}

interface EphemeralRow {
  /** Mirrors `key` — `putMany`'s adapter contract requires an `id` field. */
  id: string
  /** `_ephemeral` primary key: `${table}#${id}`. */
  key: string
  table: string
  lastAccessedAt: number
}

/**
 * Hydrate fetched pre-orders + flows into the local tables and stamp them
 * `_ephemeral` (TTL/LRU — below the kept floor, evictable, never touching
 * `_syncMeta`). See the module doc for the `atomic` trade-off. A throw
 * propagates to the caller (which maps `DatabaseClosedError` to a clean
 * `offlineMiss`); when `atomic`, it also rolls back every write.
 */
export async function hydratePreOrderRows(
  rows: PreOrderHydrationRows,
  deps: OnDemandDeps,
  { atomic }: { atomic: boolean }
): Promise<PreOrderHydrationOutcome> {
  const run = async (): Promise<PreOrderHydrationOutcome> => {
    // Sequential (not `Promise.all`) so the atomic variant stays inside
    // Dexie's transaction zone — both reads hit `_changeQueue`.
    const poShield = await deps.getActiveEntityIds("pre-orders")
    const flowShield = await deps.getActiveEntityIds("pre-order-flows")
    const pos = rows.preOrders.filter((p) => !poShield.has(p.id))
    const flows = rows.flows.filter((f) => !flowShield.has(f.id))

    if (pos.length > 0) await deps.storage.putMany(PRE_ORDER_TABLE, pos)
    if (flows.length > 0)
      await deps.storage.putMany(PRE_ORDER_FLOW_TABLE, flows)

    const now = Date.now()
    const ephemeral: EphemeralRow[] = [
      ...pos.map((p) => ephemeralMarker(PRE_ORDER_TABLE, p.id, now)),
      ...flows.map((f) => ephemeralMarker(PRE_ORDER_FLOW_TABLE, f.id, now)),
    ]
    if (ephemeral.length > 0) {
      await deps.storage.putMany(EPHEMERAL_TABLE, ephemeral)
    }
    return { pos, flows }
  }

  if (!atomic) return run()
  return deps.storage.transaction(
    [
      PRE_ORDER_TABLE,
      PRE_ORDER_FLOW_TABLE,
      EPHEMERAL_TABLE,
      CHANGE_QUEUE_TABLE,
    ],
    run
  )
}

function ephemeralMarker(table: string, id: string, at: number): EphemeralRow {
  const key = `${table}#${id}`
  return { key, id: key, table, lastAccessedAt: at }
}
